/**
 * Withheld-command marks: the lift-state record of commands VTS refused to send.
 *
 * A channel modifier with `timeout_policy` DROP holds a command until an
 * interlock confirms, and when the confirmation never comes VTS drops the
 * command and publishes an `AckOutcome.Withheld` verdict carrying it. The
 * operator pressed a button and nothing moved; without this record the floor
 * would look exactly as if the press had been lost on the way.
 *
 * Every function here is pure and returns its input array unchanged when there
 * is nothing to do, so the reducer's "nothing changed, keep the same state
 * reference" check keeps working.
 */
import { WithheldMark } from "../../interfaces/Monitoring.interface";
import { FrontEndMessageT } from "../generated/front-end/front-end-message";

/**
 * How long a withheld mark stays on a floor, in milliseconds.
 *
 * Long enough for an operator who looked away to see why their call did not
 * register, short enough that a mark never outlives the situation it reports.
 */
export const WITHHELD_MARK_TTL_MS = 30_000;

const NS_PER_MS = BigInt(1_000_000);

/**
 * The frame's timestamp in epoch milliseconds, or null when the frame carries
 * none. VTS stamps frames in nanoseconds (`timestamp_nanos`); the division stays
 * in bigint so the epoch value does not lose precision on the way to a number.
 */
export function frameTimeMs(fem: FrontEndMessageT): number | null {
  if (fem.timestamp <= BigInt(0)) return null;
  return Number(fem.timestamp / NS_PER_MS);
}

/** True while a mark is younger than the TTL at `nowMs`. */
export function isWithheldMarkLive(mark: WithheldMark, nowMs: number): boolean {
  return nowMs - mark.atMs < WITHHELD_MARK_TTL_MS;
}

/**
 * Drop marks older than the TTL, measured against `nowMs` (the newest frame's
 * time). Returns the same array when nothing was dropped.
 */
export function pruneWithheldMarks(marks: WithheldMark[], nowMs: number): WithheldMark[] {
  const kept = marks.filter((mark) => isWithheldMarkLive(mark, nowMs));
  return kept.length === marks.length ? marks : kept;
}

/**
 * Remove the marks for one floor, because a car call has now registered there
 * and the held-back press has been superseded. Returns the same array when the
 * floor had no mark.
 */
export function clearWithheldMarksAt(marks: WithheldMark[], position: number): WithheldMark[] {
  const kept = marks.filter((mark) => mark.position !== position);
  return kept.length === marks.length ? marks : kept;
}

/**
 * The newest mark on a floor that is still live at `nowMs`, or null.
 *
 * Newest by `atMs` rather than by array order, so a replayed or reordered
 * state still shows the latest command that was held back.
 */
export function liveWithheldMarkAt(
  marks: readonly WithheldMark[] | undefined,
  position: number,
  nowMs: number,
): WithheldMark | null {
  return (marks ?? []).reduce<WithheldMark | null>((newest, mark) => {
    if (mark.position !== position || !isWithheldMarkLive(mark, nowMs)) return newest;
    return newest === null || mark.atMs > newest.atMs ? mark : newest;
  }, null);
}

/** The identity the notice hook de-duplicates a mark by. */
export function withheldMarkKey(bankId: string, liftId: string, mark: WithheldMark): string {
  return `${bankId}|${liftId}|${mark.position}|${mark.atMs}`;
}
